/**
 * Record notes — the one simple-note surface.
 *
 * Four record types (form entries, calls, contacts, organizations) each grew
 * their own note UI: different button labels, different entry affordances,
 * different orderings, two different empty states. They now render through this
 * component, so a user who learns to leave a note on a call already knows how to
 * leave one on an organization.
 *
 * Deliberately presentational. Notes reach it from two unrelated stores — the
 * Supabase record_notes table for calls and form entries, activity_logs for
 * contacts and organizations — and each caller adapts its own rows to
 * SharedRecordNote. Ordering is enforced here rather than trusted from the
 * caller, because the two stores return opposite orders.
 */

import { useState } from 'react';
import type { SharedRecordNote } from '@oneup/shared';
import { SectionHeading } from './record-presentation';

export interface RecordNotesProps {
  notes?: SharedRecordNote[];
  onAddNote: (body: string) => Promise<void>;
  /** Shown above the composer. Say what this record's notes are for. */
  description?: string;
  /** Section heading. Omit to render the notes without one. */
  heading?: string | null;
  busy?: boolean;
  /**
   * Render the notes without a composer.
   *
   * For records whose Action Center already owns note entry: the history still
   * belongs on the Activity tab, but a second textarea there would be the same
   * control in two places, and a user would reasonably expect them to be two
   * different kinds of note.
   */
  readOnly?: boolean;
}

const MAX_NOTE_LENGTH = 10_000;

export function RecordNotes({
  notes = [],
  onAddNote,
  description = 'Internal context and handoff notes for this record.',
  heading = 'Notes',
  busy = false,
  readOnly = false,
}: RecordNotesProps) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const disabled = busy || saving;

  // Newest first. The two backing stores disagree on order, and "the note I
  // just wrote" must be the first one visible in both.
  const ordered = [...notes].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const save = async () => {
    const body = note.trim();
    if (!body) return;
    setSaving(true);
    setError(undefined);
    try {
      await onAddNote(body);
      setNote('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'This note could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      {heading && <SectionHeading>{heading}</SectionHeading>}
      {description && <p className="mb-2 text-xs text-muted-foreground">{description}</p>}

      {!readOnly && <>
      <textarea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Add context, decisions, or follow-up details…"
        rows={3}
        maxLength={MAX_NOTE_LENGTH}
        disabled={disabled}
        className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || !note.trim()}
          onClick={() => void save()}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add note'}
        </button>
        {note.trim() && !saving && (
          <button
            type="button"
            onClick={() => { setNote(''); setError(undefined); }}
            className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}
      </>}

      <div className="mt-3 space-y-2">
        {ordered.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
            No notes yet. The first one you add appears here.
          </p>
        ) : (
          ordered.map((item) => (
            <article key={item.id} className="rounded-md border border-border bg-background p-3">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{item.body}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {item.authorName} · {formatNoteTimestamp(item.createdAt)}
              </p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function formatNoteTimestamp(value: string): string {
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
